import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useBloodCheckout } from "@/hooks/useBloodCheckout";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { formatPrice } from "@/components/product-step/types";
import { resolveProductKey } from "@/locales";
import {
  trackPaymentModeSelect,
  trackPaymentOptionsView,
} from "@/utils/analytics";
import { useBookingStore } from "@/stores/bookingStore";
import { voucherDiscountCents } from "@/utils/voucherDiscount";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { CADENCE_CONTENTS_KEY } from "@/utils/membershipPricing";
import {
  memberOptionPricing,
  bundleScanContributionCents,
  bundleAddOnCents,
  isScanPackage,
} from "@/utils/bloodMembershipPricing";
import { useMarket } from "@/hooks/useMarket";
import { CadencePickerModal } from "@/components/booking-form/CadencePickerModal";
import { usePrepayEnabled } from "@/hooks/usePrepayEnabled";
import { X } from "lucide-react";

interface PaymentInfoProps {
  language: BookingLanguage;
  hasBloodComponent?: boolean;
  /** Suppress the "No payment required" title in the no-membership branch — the
   *  section header already shows it, so we don't repeat it. */
  hideTitle?: boolean;
}

const PaymentInfo = ({
  language,
  hasBloodComponent = false,
  hideTitle = false,
}: PaymentInfoProps) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { currency, market } = useMarket();
  const prepays = usePrepayEnabled();
  const selectedPaymentMode = useBookingFlowStore((s) => s.selectedPaymentMode);
  const setSelectedPaymentMode = useBookingFlowStore(
    (s) => s.setSelectedPaymentMode,
  );
  // Set by the CTA when the user tries to proceed without choosing — flags the
  // two option cards red and shows the hint below them. Cleared on selection.
  const paymentModeError = useBookingFlowStore((s) => s.paymentModeError);
  const showOptionError = paymentModeError && selectedPaymentMode === null;
  // Pricing for the option cards (full vs member). The checkout action itself
  // lives in the shared <PaymentCtaButton> / usePaymentStepCta.
  const { pricing, expectedMembershipCents, bundle, cart } = useBloodCheckout();
  const { selectedAddOnDetails } = useBookingFlow();
  const scanPackage = cart.find(isScanPackage);
  // Membership cadence (baseline by default) — drives the pill in the member
  // option and the "Change cadence" picker. Declared before the pricing below,
  // which needs it for the bundle baseline.
  const cadencePlan = useMembershipPlanStore((s) => s.plan);

  // A scan cart prices through the BUNDLE model, not the blood one.
  //
  // `pricing.memberTotalCents` is a sum of member prices and knows nothing about
  // the membership fee. On a scan that carries no blood draw (Core) the
  // membership is net-new — 1'890 + 299, not 1'890 — so that number quoted a
  // total the checkout would never charge: useBloodCheckout already sends
  // `expectedOneTimeCents` from the bundle, so the card said 2'009 while the
  // charge was the bundle total. (The edge function's 409 display↔charge guard
  // is what would have caught it at the till.)
  // `addsBloodTest` marks the Core-shaped case: the membership adds a yearly
  // blood test on top rather than discounting the scan, so the total goes UP and
  // "join & save" would be the wrong promise.
  const {
    oneTimeTotalCents,
    memberTotalCents,
    memberLines,
    strikeThroughCents,
    savingsCents,
    isBundle,
    addsBloodTest,
  } = memberOptionPricing({
    cart,
    bundle,
    bloodPricing: pricing,
    plan: cadencePlan,
  });

  // Itemisation for the Core-shaped card: what each line costs as a member, and
  // what it costs one-time, so the "+CHF X today" is auditable on the card
  // rather than asserted. The scan uses bundleScanContributionCents (identical
  // to the member price on Core, netted on Advanced/Pro); the standalone blood
  // test is dropped because it IS the membership.
  const bundleLines = addsBloodTest
    ? [
        ...(scanPackage
          ? [
              {
                key: "scan",
                name: scanPackage.name,
                memberCents: bundleScanContributionCents(scanPackage),
                listCents: scanPackage.default_price?.unit_amount ?? 0,
              },
            ]
          : []),
        ...selectedAddOnDetails
          .filter((a) => bundleAddOnCents(a) > 0)
          .map((a) => ({
            key: a.id,
            name: a.name,
            memberCents: bundleAddOnCents(a),
            listCents: a.default_price?.unit_amount ?? 0,
          })),
      ]
    : [];
  // Pure yearly membership (no one-time add-ons) → the member price is fully
  // recurring, so label it "/year". A mixed basket (member total exceeds the
  // cadence subscription) also bills one-time add-ons, where "/year" on the
  // total would mislead — omit it there. Never true for a bundle: the scan is
  // always a one-time line alongside the subscription.
  const isYearlyOnly =
    !isBundle && memberTotalCents === expectedMembershipCents;
  // Apply the voucher to each option's price so the cards match the summary
  // (Pay later and member price both get the rabatt). savingsCents stays the
  // membership saving (full vs member) — the voucher is a separate line.
  const discount = useBookingStore((s) => s.discount);
  const fullAfterVoucher =
    oneTimeTotalCents - voucherDiscountCents(discount, oneTimeTotalCents);
  // Over the invoice LINES, not the sum — Stripe rounds a percentage coupon
  // per line, and the card must quote the amount checkout will charge.
  const memberAfterVoucher =
    memberTotalCents - voucherDiscountCents(discount, memberLines);

  const [cadenceModalOpen, setCadenceModalOpen] = useState(false);

  // The prices the visitor actually sees on the cards — attached to the
  // upsell events so acceptance can be segmented by offer.
  const displayedPricing = {
    full_total_cents: fullAfterVoucher,
    member_total_cents: memberAfterVoucher,
    savings_cents: savingsCents,
  };

  // Upsell impression (AHD-7022): this option block IS the details-page
  // membership upsell surface — before this event nothing fired here, so
  // upsell CVR was unmeasurable. Once per mount; funnels count unique persons.
  useEffect(() => {
    if (!hasBloodComponent) return;
    trackPaymentOptionsView(cadencePlan, displayedPricing, language);
    // Fire on mount only — price/plan changes during the visit must not
    // re-emit the impression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBloodComponent]);

  if (!hasBloodComponent) {
    return (
      <div className="space-y-4">
        {!hideTitle && (
          <div className="text-xl font-medium text-gray-900 mb-4">
            {/* paymentInfo.title is "No payment needed to book" in the locale files (and
                its DE/NL equivalents) — the exact opposite of what a prepay
                market does. Both lines have to move together or the block
                contradicts itself. */}
            {t(prepays ? "paymentInfo.prepayTitle" : "paymentInfo.title")}
          </div>
        )}
        <p className="text-sm text-gray-700 font-normal font-sans">
          {/* Prepay markets (DE/NL) charge the booking up front. EUR markets
              otherwise get the euro-denominated copy (150 € fee) — except DE
              and NL, which get the same sentence without the fee clause: right
              above "Confirm booking" it read as a threat and cost
              completions. */}
          {prepays
            ? t("paymentInfo.prepayInfo")
            : t(
                market === "de" || market === "nl"
                  ? "paymentInfo.infoShort"
                  : currency === "EUR"
                    ? "paymentInfo.infoEur"
                    : "paymentInfo.info",
              )}
        </p>
      </div>
    );
  }

  // The two payment options. `afterSelect` lets the popup variant close itself
  // once a choice is made.
  const renderOptions = (afterSelect?: () => void, vertical?: boolean) => (
    <>
      <div
        id="payment-options"
        className={`grid gap-4 ${vertical ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}
      >
        {/* Left — Pay later (full price) */}
        <button
          type="button"
          onClick={() => {
            setSelectedPaymentMode("once");
            trackPaymentModeSelect(
              "once",
              "option_card",
              cadencePlan,
              displayedPricing,
              language,
            );
            afterSelect?.();
          }}
          className={`normal-case text-left rounded-xl border-2 px-5 py-5 transition-colors flex flex-col bg-white ${
            selectedPaymentMode === "once"
              ? "border-aubergine-800"
              : showOptionError
                ? "border-red-400"
                : "border-gray-200 hover:border-gray-300"
          }`}
        >
          {/* Title (left) + radio (top-right corner) on one row */}
          <div className="flex items-start justify-between gap-2">
            <span className="font-medium text-gray-900 text-lg">
              {t("paymentInfo.payLater")}
            </span>
            <div
              className={`h-5 w-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                selectedPaymentMode === "once"
                  ? "border-aubergine-800"
                  : "border-gray-300"
              }`}
            >
              {selectedPaymentMode === "once" && (
                <div className="h-2.5 w-2.5 rounded-full bg-aubergine-800" />
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {t("paymentInfo.payLaterNote")}
          </p>
          <span
            data-testid="payment-option-onetime-price"
            className="mt-3 text-xl font-bold text-gray-900"
          >
            {formatPrice(fullAfterVoucher, market)}
          </span>
        </button>

        {/* Right — Become a member and pay now (member price) */}
        <button
          type="button"
          onClick={() => {
            setSelectedPaymentMode("yearly");
            trackPaymentModeSelect(
              "yearly",
              "option_card",
              cadencePlan,
              displayedPricing,
              language,
            );
            afterSelect?.();
          }}
          className={`normal-case text-left rounded-xl border-2 px-5 py-5 transition-colors flex flex-col ${
            selectedPaymentMode === "yearly"
              ? "border-deep-green-600 bg-deep-green-100/40"
              : showOptionError
                ? "border-red-400 bg-white"
                : "border-gray-200 bg-white hover:border-gray-300"
          }`}
        >
          {/* Title (left) + radio (top-right corner) on one row */}
          <div className="flex items-start justify-between gap-2">
            <span className="font-medium text-gray-900 text-lg">
              {t(
                addsBloodTest
                  ? "paymentInfo.becomeMemberSaveAddOns"
                  : savingsCents > 0
                    ? "paymentInfo.becomeMemberPayNow"
                    : "paymentInfo.becomeMemberGetMore",
              )}
            </span>
            <div
              className={`h-5 w-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                selectedPaymentMode === "yearly"
                  ? "border-deep-green-600"
                  : "border-gray-300"
              }`}
            >
              {selectedPaymentMode === "yearly" && (
                <div className="h-2.5 w-2.5 rounded-full bg-deep-green-600" />
              )}
            </div>
          </div>
          {/* No cadence saving on the baseline plan (299, no add-ons): the "save"
              framing is false, so lead with what membership adds instead. */}
          <p className="text-sm text-gray-500 mt-2">
            {t(
              addsBloodTest
                ? "paymentInfo.addBloodTestNote"
                : savingsCents > 0
                  ? "paymentInfo.memberNote"
                  : "paymentInfo.memberNoteNoSavings",
            )}
          </p>
          {/* Cadence contents as bold green text (no pill) + a lighter "Change
            cadence" link beside it. The link is a span (not a nested button) so
            it's valid inside the option button; stops propagation so it only
            opens the picker. */}
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-semibold text-deep-blue-700">
              {t(CADENCE_CONTENTS_KEY[cadencePlan])}
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setCadenceModalOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();
                  setCadenceModalOpen(true);
                }
              }}
              className="text-sm font-normal text-gray-500 underline cursor-pointer"
            >
              {t("paymentInfo.changeCadence")}
            </span>
          </div>
          {/* Core-shaped cart: itemise what the total is made of, so the
              "+CHF X today" below is auditable on the card instead of asserted.
              (Laurent, 2026-08-06.) */}
          {bundleLines.length > 0 && (
            <div className="mt-3 flex flex-col gap-1">
              {bundleLines.map((line) => (
                <div
                  key={line.key}
                  className="flex items-baseline justify-between gap-3 text-sm"
                >
                  <span className="min-w-0 text-gray-600">
                    {resolveProductKey(language, line.name)}
                  </span>
                  <span className="shrink-0 text-gray-900">
                    {formatPrice(line.memberCents, market)}
                    {line.listCents > line.memberCents && (
                      <span className="ml-1 text-xs text-gray-400 line-through">
                        {formatPrice(line.listCents, market)}
                      </span>
                    )}
                  </span>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 text-gray-600">
                  {t(CADENCE_CONTENTS_KEY[cadencePlan])}
                </span>
                <span className="shrink-0 text-gray-900">
                  {formatPrice(expectedMembershipCents, market)}
                </span>
              </div>
            </div>
          )}
          {/* Struck old price → new price → Save badge, all on one line */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {savingsCents > 0 && strikeThroughCents !== null && (
              <span className="text-xs text-gray-400 line-through">
                {formatPrice(strikeThroughCents, market)}
              </span>
            )}
            <span
              data-testid="payment-option-member-price"
              className="text-xl font-bold text-gray-900"
            >
              {formatPrice(memberAfterVoucher, market)}
              {isYearlyOnly && (
                <span className="text-sm font-medium text-gray-500">
                  {t("bloodTracking.membership.annualSuffix")}
                </span>
              )}
            </span>
            {savingsCents > 0 && (
              <span className="rounded-full bg-light-blue-100 text-light-blue-900 text-xs font-medium px-2.5 py-0.5">
                {t("paymentInfo.save", {
                  amount: formatPrice(savingsCents, market),
                })}
              </span>
            )}
          </div>
        </button>
      </div>

      {showOptionError && (
        <p className="mt-2 text-sm font-medium text-red-500" role="alert">
          {t("paymentInfo.selectOptionError")}
        </p>
      )}

      {/* Apply = confirm the cadence (already live in the store) and, since
          picking a cadence is an explicit membership choice (compliant —
          user-initiated, not a pre-selection), select the member option so the
          basket follows. */}
      <CadencePickerModal
        isOpen={cadenceModalOpen}
        onClose={() => setCadenceModalOpen(false)}
        language={language}
        source="payment_step"
        onApply={() => setSelectedPaymentMode("yearly")}
      />
    </>
  );

  // The primary CTA is rendered once by BookingForm (and mirrored in the
  // sidebar / mobile bar) via <PaymentCtaButton>; PaymentInfo is just the
  // option cards now, so it never drifts from those buttons.
  return <div className="space-y-4">{renderOptions()}</div>;
};

export default PaymentInfo;
